/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { connect, useSelector } from "react-redux";
import { push } from "connected-react-router";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Grid,
  GridProps,
  Paper,
  Popper,
  Typography,
  Autocomplete,
  TextField
} from "@mui/material";
import queryString from "query-string";
import moment from "moment";
import { useRef } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import produce from "immer";
import * as assessmentActions from "../../../actions/assessments";
import * as NotificationActions from "../../../actions/notification";
import { t, tl } from "../../../components/translate";
import ServiceProviderSelector from "../ServiceProviderSelector";
import CreatedDate from "../CreatedDate";
import Footer from "./Footer";
import * as VitalActions from "../../../actions/vitals";
import { getReminders } from "../../../actions/reminders";
import {
  Assessment as IAssessment,
  Assessment as AssessmentInterface,
  AssessmentSettingsInterface,
  Diagnosis
} from "../../../interfaces/AssessmentInterfaces";
import { IThunkDispatch, RootState } from "../../../store";
import { Client } from "../../../interfaces/ClientInterface";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import { ExtraOralData } from "../ExtraOral";
import {
  generateTeethFirstRow,
  generateTeethSecondRow
} from "../IntraOralAdvanced/TeethMarkerComponent";
import { getInitialState } from "../IntraOralAdvanced/Tooth";
import { hasContentAdvancedIntraOral } from "../../Modals/Assessment/AdvancedIntraOralContent";
import { getBatchSymptoms } from "../../../actions/assessments";
import { TableState } from "../../../components/EditableTable/EditableTable";
import OPDDuration from "../OPDDuration";
import AssessmentClientSelect from "../AssessmentClientSelect";
import Allergies from "../Allergies";
import { ComponentLabel, ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import ClientAssessments from "../../Client/ClientInfo/ClientAssessments";
import { ReverseMap } from "../../../helpers/types";
import OPDComponents, { SortOrderType } from "./OPDComponents";
import DepartmentSelector from "../DepartmentSelector";
import useDepartmentSubscription from "../../../hooks/useDepartmentSubscription";
import { useAppDispatch } from "../../../store/hooks";
import ServiceProviderAutoSelect from "../../ServiceProvider/ServiceProviderAutoSelect";
import { getActiveClaimCode } from "../../../api/ssf";
import { notificationAdd } from "../../../actions/notification";
import { commonErrorMessage } from "../../../helpers/messages";
import useIsSSFEnabled from "../../../hooks/useIsSSFEnabled";
import { book } from "../../../api/bookings";
import Others from "../Others";
import useIsRadiographer from "../../../hooks/useIsRadiographer";
import Attachment from "../Attachment";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import FollowUp from "../FollowUp";
import DentalLabOrderForm from "../DentalLabOrder/DentalLabOrderForm";
import { DentalLabFormMode } from "../../../enum/DentalLabOrder";
import ReportsPanel from "../../Reports/Panel/ReportsPanel";
import Can from "../../Policy/Can";
import { OpdSettingsPanel } from "../../ResourceCentre/ModuleSettings/OpdSettings/OpdSettingsPanel";

// @ts-expect-error - css
import styles from "../Assessment.module.css";
import OpdVisitTypeSelector from "./OpdVisitTypeSelector";
import { AuthorizedSigners } from "../AuthorizedSigners";

export enum MODE {
  CREATE = "create",
  EDIT = "edit",
  COPY = "copy"
}

export type ModeType = ReverseMap<typeof MODE>;

interface LocationInterface {
  hash: string;
  key: string;
  pathname: string;
  search: string;
}

interface AssessmentForm {
  mode: ModeType;
  clientId: number;
  setClientId: React.Dispatch<React.SetStateAction<number>>;
  client: Client;
  userId: number;
  match: { params: { id: string; clientId: string } };
  isServiceProvider: boolean;
  resourceCentreId: number;
  spId: number;
  onSave: (
    clientId: number,
    assessmentId: number,
    serviceProviderId: number,
    data: Partial<IAssessment>,
    isSaveAsTemplate: boolean,
    templateName?: string,
    templateId?: number
  ) => void;
  getUpdatedSymptoms: () => void;
  goBack: (url: string) => void;
  assessmentSettings: AssessmentSettingsInterface;
  themeColor: string;
  location: LocationInterface;
  getVitalsById: (id: number) => void;
  vitalsCollection: [];
  getTemplates: () => void;
  assessmentCopy: AssessmentInterface;
  setAssessmentCopy: React.Dispatch<React.SetStateAction<IAssessment>>;
  tabularComponent: TableState;
  sortOrder: SortOrderType[];
}

export const LOCALSTORAGE_ASSESSMENT = "assessment";

export const getLabelForComponent = (componentLabel: ComponentLabel): string =>
  componentLabel.customLabel || componentLabel.defaultLabel;

export const advancedIntraOralInitState = {
  teethData: [
    ...generateTeethFirstRow().map((tooth, i) => ({
      shapeState: getInitialState(i, tooth.type, false),
      tooth
    })),
    ...generateTeethSecondRow().map((tooth, i) => ({
      shapeState: getInitialState(i, tooth.type, true),
      tooth
    }))
  ],
  softTissueExamination: "",
  occlusion: "",
  calculus: "",
  stain: "",
  cariesRisk: "",
  recallGap: "",
  attrition: "",
  abrasion: "",
  erosion: "",
  abfraction: "",
  bpe: new Array(18).fill("")
};

const extraOralInitState = {
  tmz: "",
  lymphNodes: "",
  otherFindings: ""
};

const GridItem = ({
  children,
  xs = 12,
  sm = 6,
  md = 6,
  /* eslint-disable react/jsx-props-no-spreading */
  ...otherProps
}: {
  children: React.ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
} & GridProps) => (
  <Grid size={{ xs, sm, md }} {...otherProps}>
    {children}
  </Grid>
);

const AssessmentForm: React.FC<AssessmentForm> = ({
  location,
  mode,
  userId,
  client,
  clientId,
  setClientId,
  isServiceProvider,
  resourceCentreId,
  spId,
  onSave,
  goBack,
  assessmentSettings,
  themeColor,
  getVitalsById,
  vitalsCollection,
  getTemplates,
  assessmentCopy,
  setAssessmentCopy,
  getUpdatedSymptoms,
  tabularComponent,
  match,
  sortOrder
}) => {
  // custom hooks
  const dispatch = useAppDispatch();
  const { isSsfEnabled } = useIsSSFEnabled();
  const isUserRadiographer = useIsRadiographer();
  const { isDepartmentSubscribed } = useDepartmentSubscription();

  // refs
  const unsavedAssessment = React.useRef(null);
  const opdTimeRef = useRef<HTMLButtonElement>(null);

  // selectors
  const templates = useSelector((state: RootState) => state.assessments.template);
  const serviceProviders = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  // local states
  const [isSsfOpd, setIsSsfOpd] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [spID, setSpID] = React.useState(isServiceProvider ? spId : "");
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = React.useState<string>("");
  const [followUpAnchorEl, setFollowUpAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showDentalLabOrderFrom, setShowDentalLabOrderFrom] = React.useState<boolean>(false);
  const [clientAssessments, setClientAssessments] = React.useState<AssessmentInterface[]>([]);

  const [showFields, setShowFields] = React.useState({
    permanent: true,
    primary: false
  });

  const [assessment, setAssessment] = React.useState<
    Partial<AssessmentInterface> & { status: string }
  >({
    createdDate: new Date(),
    symptoms: [],
    visitType: null,
    tests: [],
    services: [],
    diagnosis: [],
    attachments: [],
    medication: [],
    otherServices: [],
    followup: {
      date: new Date(),
      note: "",
      startDate: new Date(),
      endDate: new Date(new Date().getTime() + 15 * 60000)
    },
    referToSpFollowup: {
      date: new Date(),
      note: "",
      startDate: new Date(),
      endDate: new Date(new Date().getTime() + 15 * 60000)
    },
    status: "",
    assessmentImage: "",
    others: "",
    intraOral: { permanent: {}, primary: {} },
    advancedIntraOral: advancedIntraOralInitState,
    extraOral: extraOralInitState as ExtraOralData,
    provisionalDiagnosis: "",
    hopi: "",
    vitals: [],
    questionnaire: [],
    treatmentPlan: "",
    treatmentGiven: "",
    dentalDiscount: 0,
    treatmentDone: [],
    pastHistoryOfAllergy: "",
    presentHealthStatus: "",
    advice: "",
    eye: {},
    productMedication: [],
    investigation: "",
    tabularComponent,
    opdDuration: null,
    sendToClient: true,
    prescriptionAudioUrl: null,
    prescriptionAudioTranscription: "",
    prescriptionAudioSummary: "",
    departmentId: null,
    referToSpId: null,
    referToSpDepartmentId: null,
    claimCode: "",
    dentalServices: [],
    eyeServices: [],
    xrayServices: [],
    dermascopy: "",
    other1: "",
    other2: "",
    other3: "",
    skinAnalysis: [],
    authorizedSigners: {
      signerSp1: { id: null },
      signerSp2: { id: null },
      signerSp3: { id: null }
    }
  });

  // constants
  const assessmentId = Number(match?.params?.id);
  const assessmentData = assessmentId
    ? clientAssessments.find(({ id }) => assessmentId === id)
    : null;

  // helper functions
  const setData = (data: Partial<AssessmentInterface>) => {
    let newDiagnosis: Diagnosis[] = [];
    if (data.diagnosis && !Array.isArray(data.diagnosis)) {
      newDiagnosis.push(data.diagnosis);
    } else if (Array.isArray(data.diagnosis)) {
      newDiagnosis = data.diagnosis;
    }

    setAssessment({
      createdDate: data.createdDate,
      symptoms: data.symptoms || [],
      tests: data.tests || [],
      services: data.services || [],
      visitType: data.visitType || null,
      diagnosis: newDiagnosis,
      attachments: data.attachments,
      followup: data.followup
        ? data.followup
        : {
            date: null,
            note: "",
            startDate: null,
            endDate: null
          },
      referToSpFollowup: data.referToSpFollowup
        ? data.referToSpFollowup
        : {
            date: new Date(),
            note: "",
            startDate: new Date(),
            endDate: new Date(new Date().getTime() + 15 * 60000)
          },
      medication: data.medication,
      status: data.assessment || "",
      dentalDiscount: data.dentalDiscount,
      assessmentImage: data.assessmentImage,
      others: data.others,
      extraOral: data.extraOral || extraOralInitState,
      intraOral: data.intraOral || { permanent: {}, primary: {} },
      advancedIntraOral: data.advancedIntraOral || advancedIntraOralInitState,
      provisionalDiagnosis: data.provisionalDiagnosis,
      hopi: data.hopi,
      vitals: data.vitals,
      treatmentDone: data.treatmentDone || [],
      questionnaire: data.questionnaire,
      treatmentPlan: data.treatmentPlan ? data.treatmentPlan : "",
      treatmentGiven: data.treatmentGiven ? data.treatmentGiven : "",
      pastHistoryOfAllergy: data.pastHistoryOfAllergy ? data.pastHistoryOfAllergy : "",
      presentHealthStatus: data.presentHealthStatus ? data.presentHealthStatus : "",
      eye: data.eye,
      advice: data.advice,
      productMedication: data.productMedication,
      investigation: data.investigation || "",
      tabularComponent: data.tabularComponent || tabularComponent,
      opdDuration: data.opdDuration,
      sendToClient: data.sendToClient,
      prescriptionAudioUrl: data.prescriptionAudioUrl,
      prescriptionAudioTranscription: data.prescriptionAudioTranscription || "",
      prescriptionAudioSummary: data.prescriptionAudioSummary || "",
      departmentId: data.departmentId,
      referToSpId: data.referToSpId,
      referToSpDepartmentId: data.referToSpDepartmentId,
      otherServices: data.otherServices,
      dentalServices: data?.dentalServices || [],
      eyeServices: data?.eyeServices || [],
      xrayServices: data?.xrayServices || [],
      dermascopy: data?.dermascopy || "",
      other1: data?.other1 || "",
      other2: data?.other2 || "",
      other3: data?.other3 || "",
      skinAnalysis: data?.skinAnalysis || [],
      authorizedSigners: data?.authorizedSigners || {
        signerSp1: { id: null },
        signerSp2: { id: null },
        signerSp3: { id: null }
      }
    });
  };

  const onApplyTemplateClick = (selectedTemplate: AssessmentInterface | null) => {
    if (selectedTemplate) {
      setSelectedTemplateId(selectedTemplate.id);
      setSelectedTemplateName(selectedTemplate.templateName || "");
      setShowFields({
        permanent: selectedTemplate?.intraOral?.permanent
          ? !(Object.keys(selectedTemplate.intraOral.permanent).length === 0)
          : true,
        primary:
          selectedTemplate?.intraOral?.primary &&
          !(Object.keys(selectedTemplate?.intraOral?.primary).length === 0)
      });
      setData(selectedTemplate);
    } else {
      setSelectedTemplateId(null);
      setSelectedTemplateName("");
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (id) {
      try {
        await dispatch(assessmentActions.deleteAssessmentTemplate(id));
        await dispatch(assessmentActions.getAssessmentTemplates());
        dispatch(
          NotificationActions.notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "success",
            message: "Template Deleted Successfully",
            autoTimeout: true
          })
        );
        setSelectedTemplateId(null);
      } catch (e: any) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: e?.msg || "Error deleting template.",
            autoTimeout: false
          })
        );
      }
    }
  };

  // effects
  React.useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  React.useEffect(() => {
    if (clientId && isSsfOpd) {
      (async () => {
        try {
          const code = await getActiveClaimCode(clientId);
          setAssessment({ ...assessment, claimCode: code.claimCode });
        } catch (e: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: e.msg || commonErrorMessage,
              autoTimeout: false
            })
          );
        }
      })();
    }
  }, [clientId]);

  React.useEffect(() => {
    const { spid } = queryString.parse(location?.search);
    if (spid) {
      setSpID(Number(spid));
    }
  }, [location?.search]);

  React.useEffect(() => {
    if (clientId) getVitalsById(clientId);
  }, [clientId, getVitalsById]);

  React.useEffect(() => {
    // set vitals that were entered from client info panel in last 24 hours for new opd
    if (mode === MODE.CREATE && clientId) {
      setAssessment({ ...assessment, vitals: vitalsCollection });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vitalsCollection, assessmentId]);

  React.useEffect(() => {
    setShowFields({
      permanent: assessmentData?.intraOral?.permanent
        ? !(Object.keys(assessmentData.intraOral.permanent).length === 0)
        : true,
      primary:
        assessmentData?.intraOral?.primary &&
        !(Object.keys(assessmentData?.intraOral?.primary).length === 0)
    });
  }, [assessmentData]);

  React.useEffect(() => {
    if (assessmentData) {
      setData({ ...assessmentData, status: assessmentData.assessment });
      setSpID(assessmentData.serviceProviderId);
    }
  }, [assessmentData]);

  React.useEffect(() => {
    if (assessmentCopy) {
      setShowFields({
        permanent: assessmentData?.intraOral?.permanent
          ? !(Object.keys(assessmentData.intraOral.permanent).length === 0)
          : true,
        primary:
          assessmentData?.intraOral?.primary &&
          !(Object.keys(assessmentData?.intraOral?.primary).length === 0)
      });

      setData(assessmentCopy);
      setSpID(assessmentCopy.serviceProviderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentCopy]);

  React.useEffect(() => {
    if (mode === MODE.CREATE) {
      const incompleteAssessment = JSON.parse(localStorage.getItem(LOCALSTORAGE_ASSESSMENT));
      if (
        incompleteAssessment &&
        incompleteAssessment.userId === userId &&
        (!clientId || Number(clientId) === Number(incompleteAssessment?.clientId))
      ) {
        unsavedAssessment.current = incompleteAssessment;
        setShowDialog(true);
      }
    }
  }, [clientId, mode, userId]);

  React.useEffect(() => {
    if (mode === MODE.CREATE && Boolean(clientId)) {
      localStorage.setItem(
        LOCALSTORAGE_ASSESSMENT,
        JSON.stringify({
          clientId,
          clientName: `${client?.firstName} ${client?.lastName}`,
          spID,
          userId,
          data: assessment
        })
      );
    }
  }, [clientId, client, spID, userId, mode, assessment]);

  if (!assessmentSettings) return null;
  const filteredComponentLabelOrder = sortOrder.filter((item) => assessmentSettings[item]);

  return (
    <Box className={styles.assessmentFormRoot}>
      {showDialog && (
        <OkhatiDialog
          title={tl("assessment.recoverUnsavedPrescriptionTitle")}
          description={`Unsaved prescription found for ${unsavedAssessment.current.clientName}. Do you want to recover it?`}
          next={async () => {
            setClientId(unsavedAssessment.current.clientId);
            setSpID(unsavedAssessment.current.spID);
            setData(unsavedAssessment.current.data);
            setShowDialog(false);
          }}
          cancel={() => {
            localStorage.removeItem(LOCALSTORAGE_ASSESSMENT);
            setShowDialog(false);
          }}
          readMode={false}
        />
      )}

      {showDentalLabOrderFrom && (
        <DentalLabOrderForm
          mode={DentalLabFormMode.CREATE}
          match={match}
          selectedServiceProviderId={Number(spID)}
          selectedClient={client}
          onClose={() => setShowDentalLabOrderFrom(false)}
        />
      )}

      <Box
        sx={(theme) => ({
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1,
          [theme.breakpoints.up("md")]: {
            position: "absolute",
            top: theme.spacing(6),
            right: theme.spacing(2),
            zIndex: 3
          },
          px: 2,
          pt: 1
        })}
      >
        <Box width={{ xs: "100%", sm: 200 }}>
          <Autocomplete
            sx={{ padding: 0 }}
            options={Array.isArray(templates) ? templates : []}
            getOptionLabel={(option) => option.templateName || "Unnamed Template"}
            renderOption={(params, option) => (
              <li {...params} data-testmation="applyTemplateOption">
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography>{option?.templateName || "Unnamed Template"}</Typography>
                  <Box
                    component="span"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleDeleteTemplate(option.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </Box>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                data-testmation="applyTemplate"
                placeholder="Apply Template"
                variant="outlined"
                size="small"
                sx={{ padding: 0 }}
              />
            )}
            onChange={(_, newValue) => onApplyTemplateClick(newValue)}
          />
        </Box>

        <Button
          variant="outlined"
          size="small"
          sx={{
            height: 35,
            borderColor: "divider"
          }}
          onClick={() => setShowDentalLabOrderFrom(true)}
        >
          Create Dental Lab Order
        </Button>

        <Can policyAccessKey="opd:changeSettings">
          <OpdSettingsPanel />
        </Can>
        <ReportsPanel module="opd" />
      </Box>

      {/* ssf checkbox */}
      {isSsfEnabled && (
        <Box px={2} display="flex" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isSsfOpd}
                onChange={() => {
                  setIsSsfOpd(!isSsfOpd);
                  setClientId(null);
                  setAssessment({ ...assessment, claimCode: "" });
                }}
              />
            }
            label="Is SSF"
          />
          {isSsfOpd && clientId && (
            <Typography>
              {`Claim code: ${
                assessment.claimCode || "Not found, please create bill for opd first."
              }`}
            </Typography>
          )}
        </Box>
      )}

      <Grid
        container
        spacing={1}
        sx={{
          px: 2,
          py: 1
        }}
      >
        <GridItem sm={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <AssessmentClientSelect
                clientId={clientId}
                setClientId={setClientId}
                isDisabled={mode === MODE.EDIT}
                width="280px"
              />
              <OpdVisitTypeSelector
                visitType={assessment.visitType}
                onVisitTypeChange={(type) =>
                  setAssessment((prevState) => ({ ...prevState, visitType: type }))
                }
              />
            </Box>
            {!isServiceProvider && !assessmentData?.serviceProviderId && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <ServiceProviderSelector
                  width={isDepartmentSubscribed ? "280px" : "320px"}
                  resourceCentreId={resourceCentreId}
                  spID={Number(spID)}
                  onChange={(v) => setSpID(v)}
                />

                {isDepartmentSubscribed && (
                  <DepartmentSelector
                    width="150px"
                    initialValue={
                      serviceProviders.find((item: ServiceProvider) => item.id === spID)
                        ?.departmentId || assessment.departmentId
                    }
                    onChange={(id) => setAssessment({ ...assessment, departmentId: id })}
                  />
                )}
              </Box>
            )}

            {!isServiceProvider && !assessmentData?.referToSpId && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box width={isDepartmentSubscribed ? "280px" : "320px"}>
                  <ServiceProviderAutoSelect
                    isClearable
                    disableDefaultError
                    onChange={(v) => {
                      if (!Array.isArray(v)) {
                        setData({ ...assessment, referToSpId: v });
                      }
                    }}
                    serviceProviderId={Number(assessment?.referToSpId)}
                    label={t("assessment.referToSp")}
                  />
                </Box>

                {isDepartmentSubscribed && (
                  <DepartmentSelector
                    width="150px"
                    initialValue={(() => {
                      const sp = assessment.referToSpId
                        ? serviceProviders.find(
                            (item: ServiceProvider) => item.id === Number(assessment.referToSpId)
                          )
                        : null;

                      return sp?.departmentId || assessment?.referToSpDepartmentId || null;
                    })()}
                    onChange={(id) => setAssessment({ ...assessment, referToSpDepartmentId: id })}
                  />
                )}

                <Button
                  size="small"
                  variant="outlined"
                  disabled={!assessment.referToSpId}
                  onClick={(e) => setFollowUpAnchorEl(followUpAnchorEl ? null : e.currentTarget)}
                >
                  Create follow up booking?
                </Button>
                <Popper
                  id={followUpAnchorEl ? "followUpPopper" : undefined}
                  open={Boolean(followUpAnchorEl)}
                  anchorEl={followUpAnchorEl}
                  placement="bottom-start"
                  sx={{
                    zIndex: 1,
                    maxWidth: "calc(100vw - 32px)",
                    width: { xs: "calc(100vw - 32px)", md: "auto" }
                  }}
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8]
                      }
                    }
                  ]}
                >
                  <ClickAwayListener onClickAway={() => setFollowUpAnchorEl(null)}>
                    <Paper>
                      <Box
                        sx={(theme) => ({
                          p: 2,
                          border: "1px solid",
                          borderColor: theme.palette.divider
                        })}
                      >
                        <FollowUp
                          assessmentFollowUp={assessment.referToSpFollowup as any}
                          onChange={(updatedValues) => {
                            setAssessment({
                              ...assessment,
                              referToSpFollowup: {
                                ...(assessment.referToSpFollowup as any),
                                ...updatedValues
                              }
                            });
                          }}
                        />
                      </Box>
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <CreatedDate
                width={isDepartmentSubscribed ? "280px" : "150px"}
                createdDate={assessment && assessment.createdDate}
                onChange={(v) => {
                  setAssessment({ ...assessment, createdDate: v });
                }}
              />
              {assessmentSettings.opdDuration && (
                <OPDDuration
                  opdTimeRef={opdTimeRef}
                  assessmentId={assessmentId}
                  opdDuration={assessment?.opdDuration}
                />
              )}
            </Box>

            {assessmentSettings?.authorizedSigners && (
              <AuthorizedSigners
                signers={assessment?.authorizedSigners}
                onChange={(v) => setAssessment({ ...assessment, authorizedSigners: v })}
                spID={Number(spID)}
              />
            )}
          </Box>
        </GridItem>

        {/* allergies */}
        <GridItem sm={12}>
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "column",
              [theme.breakpoints.down("md")]: {
                alignItems: "flex-start"
              }
            })}
          >
            <Allergies clientId={client?.id} />
          </Box>
        </GridItem>

        {/* empty element to reserve a whole row */}
        <Grid size={{ xs: 12 }}>
          <Divider />
        </Grid>
      </Grid>

      <Box
        sx={(theme) => ({
          display: "flex",
          gap: 1,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column-reverse"
          }
        })}
      >
        {/* form inputs */}
        {!isUserRadiographer && (
          <Box
            sx={{
              padding: 2,
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {filteredComponentLabelOrder.map((item) => (
              <OPDComponents
                key={item}
                component={item}
                assessment={assessment}
                setAssessment={setAssessment}
                spID={spID}
                assessmentId={assessmentId}
                clientId={clientId}
                themeColor={themeColor}
                tabular={{
                  initValue: assessment.tabularComponent || tabularComponent,
                  title: tabularComponent.title
                }}
                intraOral={{ showFields, setShowFields }}
              />
            ))}
          </Box>
        )}

        {isUserRadiographer && (
          <Box
            sx={{
              padding: 2,
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Attachment
              assessmentId={assessmentId}
              onfilesChange={(files) => {
                setAssessment({ ...assessment, attachments: files });
              }}
            />

            <Others
              label="Remarks"
              assessmentOthers={assessment?.others}
              onChange={(v) => {
                setAssessment({ ...assessment, others: v });
              }}
            />
          </Box>
        )}
        {/* history sidebar */}
        <Box
          component={Paper}
          sx={(theme) => ({
            width: "30%",
            px: 2,
            [theme.breakpoints.down("md")]: {
              width: "100%",
              maxHeight: "70vh",
              overflowY: "auto",
              overflowX: "hidden",
              position: "relative"
            }
          })}
        >
          <ClientAssessments
            clientId={clientId}
            setAssessmentCopy={setAssessmentCopy}
            numberOfHistoryToLoad={10}
            afterFetch={(list) => setClientAssessments(list)}
            hidePrescriptionSummaryPrint
          />
        </Box>
      </Box>

      <Footer
        mode={mode}
        sendToClient={assessment.sendToClient}
        onSendToClientChange={() =>
          setAssessment((prevState) => ({ ...assessment, sendToClient: !prevState.sendToClient }))
        }
        onSave={async () => {
          const isSaveAsTemplate = false;
          localStorage.removeItem(LOCALSTORAGE_ASSESSMENT);
          const updatedAssessment = produce(assessment, (draft) => {
            draft.opdDuration = opdTimeRef.current ? Number(opdTimeRef.current.value) : null;
          });
          // eslint-disable-next-line max-len
          await onSave(clientId, assessmentId, Number(spID), updatedAssessment, isSaveAsTemplate);
          await getVitalsById(clientId);
          if (assessment.symptoms.some((item) => !item.code.length)) {
            await getUpdatedSymptoms();
          }

          // if follow up day in greater than 0 then create/update followup booking
          if (
            mode === MODE.CREATE &&
            assessment?.followup?.date &&
            moment(assessment.followup.date).isAfter(moment(), "day")
          ) {
            const { startDate, endDate, note } = assessment.followup;

            if (!startDate || !endDate) {
              return;
            }

            const start = new Date(startDate);
            const end = new Date(endDate);

            const bookingData = {
              resourceCentreId,
              serviceProviderId: spID,
              departmentId: assessment.departmentId,
              clientId: clientId || client?.id,
              start,
              end,
              clientName: `${client?.firstName} ${client?.lastName}`,
              clientPhoneNo: client?.phone,
              clientEmail: client?.email,
              clientGender: client?.gender,
              clientAddress: client?.address,
              followUp: true,
              remarks: note,
              // using default valuse for paymentInfo, sendToClient, sendToSp and services
              paymentInfo: { amount: 0, isPaid: false, required: true },
              sendToClient: true,
              sendToSp: true,
              services: []
            };
            await book(bookingData);
          }

          // if referToSpFollowup is checked, create the followup booking
          if (
            mode === MODE.CREATE &&
            assessment?.referToSpFollowup?.date &&
            assessment?.referToSpId
          ) {
            const { startDate, endDate, note } = assessment.referToSpFollowup;

            if (!startDate || !endDate) {
              return;
            }

            const referToSpStart = new Date(startDate);
            const referToSpEnd = new Date(endDate);

            const referBookingData = {
              resourceCentreId,
              serviceProviderId: assessment.referToSpId,
              departmentId: assessment.referToSpDepartmentId,
              clientId: clientId || client?.id,
              start: referToSpStart,
              end: referToSpEnd,
              clientName: `${client?.firstName} ${client?.lastName}`,
              clientPhoneNo: client?.phone,
              clientEmail: client?.email,
              clientGender: client?.gender,
              clientAddress: client?.address,
              followUp: true,
              remarks: note,
              paymentInfo: { amount: 0, isPaid: false, required: true },
              sendToClient: true,
              sendToSp: true,
              services: []
            };

            await book(referBookingData);
          }
        }}
        onTemplateSave={async (name: string) => {
          const isSaveAsTemplate = true;
          await onSave(clientId, assessmentId, Number(spID), assessment, isSaveAsTemplate, name);
        }}
        onTemplateUpdate={async (name: string) => {
          if (selectedTemplateId) {
            const isSaveAsTemplate = true;
            await onSave(
              clientId,
              assessmentId,
              Number(spID),
              assessment,
              isSaveAsTemplate,
              name,
              selectedTemplateId
            );
          }
        }}
        selectedTemplateId={selectedTemplateId || undefined}
        selectedTemplateName={selectedTemplateName}
        saveDisabled={!spID || !clientId}
        goBack={() => goBack(`/clients/${clientId}`)}
        printJSX={undefined}
      />
    </Box>
  );
};

const mapStateToProps = ({ userContext, vitals, clients, resources }: RootState, ownProps) => {
  const currentResourceCentre: ResourceCentre = resources.resourceCentres.find(
    (item: ResourceCentre) => item.id === userContext.resourceCentreId
  );
  const isServiceProvider = userContext.userCreds?.authenticable === "serviceProvider" || false;
  const userId = Number(userContext.userCreds.id);
  const spId = isServiceProvider && Number(userContext.user.id);
  const client = clients.collection?.find(({ id }) => Number(ownProps.clientId) === id);
  const assessmentSettings = currentResourceCentre?.settings.assessmentSettings.components;
  const tabularComponent = currentResourceCentre?.settings.assessmentSettings.tabularComponent;
  const themeColor = currentResourceCentre?.settings.lookAndFeel.primaryColour;
  const vitalsCollection = vitals.collection;
  const sortOrder = currentResourceCentre?.settings.assessmentSettings.sortOrder;
  return {
    spId,
    userId,
    client,
    isServiceProvider,
    assessmentSettings,
    themeColor,
    vitalsCollection,
    tabularComponent,
    sortOrder
  };
};

export default connect(mapStateToProps, (dispatch: IThunkDispatch) => ({
  getVitalsById: (id: number) => dispatch(VitalActions.getVitalsById(id)),
  goBack: (url: string) => {
    dispatch(push(url));
  },
  getUpdatedSymptoms: async () => {
    await dispatch(getBatchSymptoms(true));
  },
  onSave: async (
    clientId: number,
    assessmentId: number,
    spId: number,
    assessment: Partial<AssessmentInterface> & { status: string },
    isSaveAsTemplate: boolean,
    templateName?: string,
    templateId?: number
  ) => {
    const {
      assessmentImage,
      createdDate,
      diagnosis,
      symptoms,
      tests,
      services,
      attachments,
      followup,
      medication,
      visitType,
      others,
      intraOral,
      extraOral,
      advancedIntraOral,
      provisionalDiagnosis,
      hopi,
      vitals,
      questionnaire,
      treatmentPlan,
      treatmentGiven,
      treatmentDone,
      pastHistoryOfAllergy,
      presentHealthStatus,
      eye,
      advice,
      productMedication,
      investigation,
      tabularComponent,
      opdDuration,
      dentalDiscount,
      sendToClient,
      prescriptionAudioUrl,
      prescriptionAudioTranscription,
      prescriptionAudioSummary,
      departmentId,
      referToSpId,
      referToSpDepartmentId,
      claimCode,
      otherServices,
      dentalServices,
      eyeServices,
      xrayServices,
      referToSpFollowup,
      dermascopy,
      other1,
      other2,
      other3,
      skinAnalysis,
      authorizedSigners
    } = assessment;

    if (!spId && !isSaveAsTemplate) {
      dispatch(
        NotificationActions.notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: tl("assessment.noSPError"),
          autoTimeout: true
        })
      );

      return;
    }

    // we dont create followup booking if followup date is before or same day as current date
    // that means we dont also have to create reminder for such followup

    // as sending followup i.e. even an empty object causes reminder to be created,
    // so we send null such that the reminder is also not created

    const followUpData = moment(followup?.date).isAfter(moment(), "day") ? followup : null;

    const assessmentData = {
      clientId,
      assessment: assessment.status,
      createdDate,
      assessmentImage: assessmentImage || "",
      diagnosis,
      symptoms,
      tests,
      services,
      attachments: attachments?.map((item: any) => item.uuid),
      followup: followUpData,
      referToSpFollowup,
      medication,
      others,
      intraOral,
      visitType,
      treatmentDone,
      advancedIntraOral: hasContentAdvancedIntraOral(advancedIntraOral) ? advancedIntraOral : null,
      extraOral,
      provisionalDiagnosis,
      hopi,
      otherServices,
      dentalDiscount,
      vitals,
      questionnaire,
      serviceProviderId: spId,
      treatmentPlan,
      treatmentGiven,
      pastHistoryOfAllergy,
      presentHealthStatus,
      eye,
      advice,
      productMedication,
      investigation,
      tabularComponent,
      opdDuration,
      sendToClient,
      prescriptionAudioUrl,
      prescriptionAudioTranscription,
      prescriptionAudioSummary,
      departmentId,
      referToSpId,
      referToSpDepartmentId,
      claimCode,
      dentalServices,
      eyeServices,
      xrayServices,
      dermascopy,
      other1,
      other2,
      other3,
      skinAnalysis,
      authorizedSigners
    };

    assessmentData.vitals?.forEach((vital) => {
      // eslint-disable-next-line no-param-reassign
      delete vital.type;
    });

    // if saving as template
    if (isSaveAsTemplate) {
      if (templateId) {
        await dispatch(
          assessmentActions.updateAssessmentTemplate({
            assessmentId: templateId,
            assessmentData: { ...assessmentData, templateName } as AssessmentInterface
          })
        );
      } else {
        await dispatch(
          assessmentActions.createAssessmentTemplate({
            ...assessmentData,
            createdDate: null,
            templateName
          })
        );
      }
      await dispatch(assessmentActions.getAssessmentTemplates());
    } else if (!isSaveAsTemplate) {
      // if assessmentId is provided, and not saving as template, update the assessment
      if (assessmentId) {
        await dispatch(
          assessmentActions.updateAssessment({
            assessmentId,
            assessmentData: assessmentData as AssessmentInterface
          })
        );
      } else {
        await dispatch(assessmentActions.createAssessment(assessmentData));
      }
    }
    dispatch(getReminders());
  },
  getTemplates: async () => {
    await dispatch(assessmentActions.getAssessmentTemplates());
  }
}))(AssessmentForm);
